import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/lib/stores/auth-store'
import { getRedmineBaseUrl } from '@/lib/tenant-config'
import { fetchWithAuthHandler, handleApiError } from '@/lib/api-error-handler'

// Enhanced project interface to support Redmine API structure
export interface EnhancedProject {
    id: number
    name: string
    identifier: string
    description?: string
    status: number
    is_public: boolean
    parent?: {
        id: number
        name: string
    }
    created_on: string
    updated_on: string
    children?: EnhancedProject[]
    level?: number
    homepage?: string
    inherit_members?: boolean
}

export interface ProjectsData {
    projects: EnhancedProject[]
    total_count?: number
}

// Helper function to get authentication headers
const getAuthHeaders = (session: any) => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json'
    }

    // Add authentication
    if (session?.credentials?.basicAuth) {
        headers['Authorization'] = `Basic ${session.credentials.basicAuth}`
    } else if (session?.apiKey) {
        headers['X-Redmine-API-Key'] = session.apiKey
    } else {
        return null
    }

    // Add base URL from tenant configuration
    const baseUrl = getRedmineBaseUrl()
    headers['X-Redmine-Base-URL'] = baseUrl

    return headers
}

// Build hierarchical project tree structure
const buildProjectTree = (projects: EnhancedProject[]): EnhancedProject[] => {
    const projectMap = new Map<number, EnhancedProject>()
    const rootProjects: EnhancedProject[] = []

    // First pass: create map with all projects and initialize children arrays
    projects.forEach(project => {
        projectMap.set(project.id, {
            ...project,
            children: []
        })
    })

    // Second pass: build parent-child relationships from API data
    projects.forEach(project => {
        const projectNode = projectMap.get(project.id)!

        if (project.parent?.id) {
            // This is a child project - add to parent's children
            const parent = projectMap.get(project.parent.id)
            if (parent) {
                parent.children = parent.children || []
                parent.children.push(projectNode)
            } else {
                // Parent not found - treat as root
                rootProjects.push(projectNode)
            }
        } else {
            // No parent - this is a root project
            rootProjects.push(projectNode)
        }
    })

    return rootProjects
}

// Fetch all projects with pagination
const fetchAllProjects = async (session: any): Promise<ProjectsData> => {
    const authHeaders = getAuthHeaders(session)
    if (!authHeaders) {
        throw new Error('No authentication headers available')
    }

    const PROJECTS_PER_PAGE = 100
    let allProjects: any[] = []
    let currentOffset = 0
    let iterations = 0
    const maxIterations = 50

    // Fetch ALL projects to build complete hierarchy
    while (iterations < maxIterations) {
        iterations++

        const response = await fetchWithAuthHandler(`/api/projects?limit=${PROJECTS_PER_PAGE}&offset=${currentOffset}`, {
            headers: { ...authHeaders }
        })

        if (!response.ok) {
            throw new Error(`Failed to fetch projects: ${response.status}`)
        }

        const batchData = await response.json()

        if (!batchData.projects || batchData.projects.length === 0) {
            break
        }

        allProjects = [...allProjects, ...batchData.projects]

        if (batchData.projects.length < PROJECTS_PER_PAGE) {
            break
        }

        currentOffset += PROJECTS_PER_PAGE
    }

    // Build the hierarchical structure
    const hierarchicalProjects = buildProjectTree(allProjects)

    return {
        projects: hierarchicalProjects,
        total_count: allProjects.length
    }
}

// React Query hook for projects data
export function useProjects() {
    const { session } = useAuthStore()

    return useQuery({
        queryKey: ['projects', session?.apiKey || session?.credentials?.basicAuth],
        queryFn: () => fetchAllProjects(session),
        enabled: !!session,
        staleTime: 5 * 60 * 1000, // 5 minutes - projects don't change often
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: 2,
    })
}
